import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import axios from 'axios';
import { useAuth } from '../App';
import { API, getImageUrl } from '../utils';
import { Search, MapPin, Star, Filter, X, TrendingUp, BadgeCheck, Award, CalendarIcon, Plane } from 'lucide-react';
import { format } from 'date-fns';

const BrowseCreatives = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [specialties, setSpecialties] = useState({ photography: [], videography: [] });
  const [filters, setFilters] = useState({
    specialty: searchParams.get('specialty') || '',
    city: searchParams.get('city') || '',
    date: searchParams.get('date') || '',
    minRate: 0,
    maxRate: 500,
    minRating: 0,
    badge: searchParams.get('badge') || ''
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const { user } = useAuth();

  const badgeConfig = {
    trending:  { label: 'Trending',   icon: TrendingUp, color: 'bg-orange-100 text-orange-700 border-orange-200' },
    verified:  { label: 'Verified',   icon: BadgeCheck, color: 'bg-blue-100 text-blue-700 border-blue-200' },
    featured:  { label: 'Featured',   icon: Award,      color: 'bg-purple-100 text-purple-700 border-purple-200' },
    top_rated: { label: 'Top Rated',  icon: Star,       color: 'bg-amber-100 text-amber-700 border-amber-200' }
  };

  useEffect(() => {
    fetchSpecialties();
    fetchCreatives();
  }, []);

  const fetchSpecialties = async () => {
    try {
      const response = await axios.get(`${API}/specialties`);
      setSpecialties(response.data);
    } catch (error) {
      console.error('Error fetching specialties:', error);
    }
  };

  const fetchCreatives = async (customFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (customFilters.specialty) params.append('specialty', customFilters.specialty);
      if (customFilters.city) params.append('city', customFilters.city);
      if (customFilters.date) params.append('travel_date', customFilters.date);
      if (customFilters.minRate > 0) params.append('min_rate', customFilters.minRate);
      if (customFilters.maxRate < 500) params.append('max_rate', customFilters.maxRate);
      if (customFilters.minRating > 0) params.append('min_rating', customFilters.minRating);
      if (customFilters.badge) params.append('badge', customFilters.badge);
      const response = await axios.get(`${API}/creatives?${params.toString()}`);
      setCreatives(response.data);
    } catch (error) {
      console.error('Error fetching creatives:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchCreatives(filters);

  const clearFilters = () => {
    const cleared = { specialty: '', city: '', date: '', minRate: 0, maxRate: 500, minRating: 0, badge: '' };
    setFilters(cleared);
    setSelectedDate(null);
    fetchCreatives(cleared);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setFilters({ ...filters, date: date ? format(date, 'yyyy-MM-dd') : '' });
  };

  const allSpecialties = [...specialties.photography, ...specialties.videography];
  const hasActiveFilters = filters.date || filters.city || filters.specialty || filters.badge || filters.minRate > 0 || filters.maxRate < 500 || filters.minRating > 0;

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      <Helmet>
        <title>Browse Denver Photographers &amp; Videographers | FrameFinder</title>
        <meta name="description" content="Browse vetted local photographers and videographers in Denver. Filter by specialty, availability, and rate. Book with confidence on FrameFinder." />
        <link rel="canonical" href="https://www.framefinder.io/photographers" />
      </Helmet>

      {/* ── Page Header ── */}
      <div className="bg-white border-b border-[#e4e1db] px-5 md:px-10 pt-7 pb-6">
        <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-1.5">Explore</p>
        <h1 className="font-display font-normal text-[28px] md:text-[32px] text-[#0e0e0e] tracking-[-0.5px] leading-[1.1] mb-5">
          Browse Photographers
        </h1>

        {/* Search row */}
        <div className="flex flex-col md:flex-row gap-3">
          {/* City search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c0bdb8]" />
            <Input
              placeholder="Search by city..."
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-9 h-10 text-[14px] text-[#0e0e0e] border-[#e4e1db] placeholder:text-[#c0bdb8] rounded-full focus-visible:ring-0 focus-visible:border-[#0e0e0e]"
              data-testid="search-city-input"
            />
          </div>

          {/* Date picker */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                className={`inline-flex items-center gap-2 px-4 h-10 rounded-full border text-[13px] font-medium transition-colors w-full md:w-auto ${
                  selectedDate
                    ? 'border-[#0e0e0e] text-[#0e0e0e] bg-white'
                    : 'border-[#e4e1db] text-[#a0a0a0] bg-white hover:border-[#0e0e0e] hover:text-[#0e0e0e]'
                }`}
                data-testid="date-picker-btn"
              >
                <CalendarIcon className="w-3.5 h-3.5 flex-shrink-0" />
                {selectedDate ? format(selectedDate, 'MMM d, yyyy') : 'Select date'}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                initialFocus
                disabled={(date) => date < new Date()}
              />
            </PopoverContent>
          </Popover>

          {/* Specialty select */}
          <Select value={filters.specialty || 'all'} onValueChange={(v) => setFilters({ ...filters, specialty: v === 'all' ? '' : v })}>
            <SelectTrigger
              className="w-full md:w-52 h-10 text-[13px] border-[#e4e1db] rounded-full focus:ring-0 focus:border-[#0e0e0e]"
              data-testid="specialty-select"
            >
              <SelectValue placeholder="All Specialties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Specialties</SelectItem>
              {allSpecialties.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Search button */}
          <button
            onClick={handleSearch}
            className="inline-flex items-center justify-center gap-1.5 px-5 h-10 rounded-full bg-[#0e0e0e] text-white text-[13px] font-medium hover:bg-[#222] transition-colors"
            data-testid="search-btn"
          >
            <Search className="w-3.5 h-3.5" />
            Search
          </button>

          {/* Mobile filters toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center justify-center gap-1.5 px-4 h-10 rounded-full border text-[13px] font-medium transition-colors md:hidden ${
              showFilters ? 'border-[#0e0e0e] text-[#0e0e0e]' : 'border-[#e4e1db] text-[#5a5a5a]'
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
            Filters
          </button>
        </div>

        {/* Advanced Filters */}
        <div className={`mt-4 pt-4 border-t border-[#e4e1db] ${showFilters ? 'block' : 'hidden md:block'}`}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-[12px] font-semibold text-[#0e0e0e]">Filters</p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-1 text-[12px] text-[#a0a0a0] hover:text-[#0e0e0e] transition-colors"
              >
                <X className="w-3 h-3" />
                Clear all
              </button>
            )}
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-2 block">
                Hourly Rate: ${filters.minRate} – ${filters.maxRate}
              </label>
              <Slider
                value={[filters.minRate, filters.maxRate]}
                onValueChange={([min, max]) => setFilters({ ...filters, minRate: min, maxRate: max })}
                min={0}
                max={500}
                step={10}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-2 block">Minimum Rating</label>
              <Select value={String(filters.minRating) || '0'} onValueChange={(v) => setFilters({ ...filters, minRating: Number(v) })}>
                <SelectTrigger className="h-9 text-[13px] border-[#e4e1db] rounded-[8px] focus:ring-0 focus:border-[#0e0e0e]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Any rating</SelectItem>
                  <SelectItem value="3">3+ stars</SelectItem>
                  <SelectItem value="4">4+ stars</SelectItem>
                  <SelectItem value="4.5">4.5+ stars</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-2 block">Badge</label>
              <Select value={filters.badge || 'all'} onValueChange={(v) => setFilters({ ...filters, badge: v === 'all' ? '' : v })}>
                <SelectTrigger className="h-9 text-[13px] border-[#e4e1db] rounded-[8px] focus:ring-0 focus:border-[#0e0e0e]" data-testid="badge-filter">
                  <SelectValue placeholder="All Creatives" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Creatives</SelectItem>
                  <SelectItem value="trending">🔥 Trending</SelectItem>
                  <SelectItem value="verified">✓ Verified</SelectItem>
                  <SelectItem value="featured">⭐ Featured</SelectItem>
                  <SelectItem value="top_rated">🏆 Top Rated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* ── Results ── */}
      <div className="px-5 md:px-10 py-6">

        {/* Results meta row */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[13px] text-[#a0a0a0]">
              {loading ? 'Loading...' : `${creatives.length} creative${creatives.length !== 1 ? 's' : ''} found`}
            </p>
            {filters.date && (
              <p className="text-[11px] text-[#c0bdb8] mt-0.5">
                Available on {format(new Date(filters.date), 'MMMM d, yyyy')}
                {filters.city && ` in ${filters.city}`}
              </p>
            )}
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-[12px] text-[#a0a0a0] hover:text-[#0e0e0e] transition-colors"
            >
              <X className="w-3 h-3" />
              Clear filters
            </button>
          )}
        </div>

        {/* Loading skeleton */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <div key={i} className="animate-pulse bg-white border border-[#e4e1db] rounded-[12px] overflow-hidden">
                <div className="aspect-[3/4] bg-[#f0ede8]" />
                <div className="p-3 space-y-1.5">
                  <div className="h-3 bg-[#f0ede8] rounded w-3/4" />
                  <div className="h-2.5 bg-[#f0ede8] rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>

        ) : creatives.length === 0 ? (
          /* Empty state */
          <div className="text-center py-20">
            <div className="w-14 h-14 rounded-full bg-white border border-[#e4e1db] flex items-center justify-center mx-auto mb-4">
              <Search className="w-6 h-6 text-[#c0bdb8]" />
            </div>
            <h3 className="font-display font-normal text-[22px] text-[#0e0e0e] tracking-[-0.3px] mb-1">No creatives found</h3>
            <p className="text-[13px] text-[#a0a0a0] mb-5">Try adjusting your filters or search criteria</p>
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#e4e1db] text-[#5a5a5a] text-[12px] font-medium hover:border-[#0e0e0e] hover:text-[#0e0e0e] transition-colors"
            >
              Clear filters
            </button>
          </div>

        ) : (
          /* Creative cards grid */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {creatives.map((creative) => (
              <Link
                key={creative.profile_id}
                to={`/photographers/${(creative.user_name || 'photographer').replace(/\s+/g, '')}_${creative.profile_id.replace('profile_', '')}`}
                className="group bg-white border border-[#e4e1db] rounded-[12px] overflow-hidden hover:border-[#0e0e0e] hover:shadow-[0_4px_16px_rgba(14,14,14,0.08)] transition-all"
                data-testid={`creative-card-${creative.profile_id}`}
              >
                {/* Image */}
                <div className="relative overflow-hidden aspect-[3/4]">
                  {creative.user_picture ? (
                    <img
                      src={getImageUrl(creative.user_picture)}
                      alt={creative.user_name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : creative.portfolio_items?.[0]?.url ? (
                    <img
                      src={getImageUrl(creative.portfolio_items[0].url)}
                      alt={creative.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#f0ede8] flex items-center justify-center">
                      <span className="font-display font-normal text-[32px] text-[#d0cdc8]">
                        {(creative.user_name || 'C')[0]}
                      </span>
                    </div>
                  )}

                  {/* Badges — icon only on small, label on sm+ */}
                  <div className="absolute top-1.5 left-1.5 flex flex-wrap gap-1">
                    {creative.is_traveling && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold border bg-green-100 text-green-700 border-green-200">
                        <Plane className="w-2 h-2" />
                        <span className="hidden sm:inline">Traveling</span>
                      </span>
                    )}
                    {creative.badges?.slice(0, 1).map((badge) => {
                      const cfg = badgeConfig[badge];
                      if (!cfg) return null;
                      const Icon = cfg.icon;
                      return (
                        <span
                          key={badge}
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold border ${cfg.color}`}
                        >
                          <Icon className="w-2 h-2" />
                          <span className="hidden sm:inline">{cfg.label}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Card body */}
                <div className="p-2.5">
                  <div className="flex items-center justify-between mb-0.5">
                    <h3 className="font-semibold text-[12px] text-[#0e0e0e] truncate mr-1 leading-tight">
                      {creative.user_name || 'Photographer'}
                    </h3>
                    {creative.rating > 0 && (
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <Star className="w-2.5 h-2.5 fill-[#d4a843] text-[#d4a843]" />
                        <span className="text-[11px] font-medium text-[#0e0e0e]">
                          {creative.rating.toFixed(1)}
                        </span>
                      </div>
                    )}
                  </div>

                  <p className="text-[10px] text-[#a0a0a0] truncate leading-tight mb-1.5">{creative.title}</p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-0.5 text-[10px] text-[#a0a0a0] min-w-0 mr-1">
                      <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
                      <span className="truncate">{creative.city || '—'}</span>
                    </div>
                    <span className="text-[11px] font-semibold text-[#0e0e0e] flex-shrink-0">${creative.hourly_rate}/hr</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseCreatives;
